import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CrmLayout from "../../components/CrmLayout";
import api from "../../lib/api";
import { prettyDate, money } from "../../lib/format";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { ArrowLeft, Mail, Phone } from "lucide-react";

const STATUS_STYLE = {
  scheduled: "bg-secondary text-secondary-foreground", confirmed: "bg-primary/10 text-primary",
  completed: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]", cancelled: "bg-destructive/10 text-destructive",
  no_show: "bg-muted text-muted-foreground", pending_deposit: "bg-accent/20 text-accent-foreground",
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/clients/${id}`).then((r) => setData(r.data)).catch(() => {}); }, [id]);

  if (!data) return <CrmLayout title="Client"><div className="text-muted-foreground">Loading…</div></CrmLayout>;
  const { client, appointments } = data;
  const intake = client.intake || {};

  return (
    <CrmLayout title="Client profile" actions={<Button variant="ghost" onClick={() => navigate("/clients")}><ArrowLeft size={16} className="mr-1" /> Back</Button>}>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center font-display text-2xl font-bold mb-4">{client.name?.[0]?.toUpperCase()}</div>
            <h2 className="font-display text-2xl font-semibold">{client.name}</h2>
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><Mail size={14} /> {client.email || "—"}</div>
              <div className="flex items-center gap-2"><Phone size={14} /> {client.phone || "—"}</div>
            </div>
          </div>
          {(intake.hair_type || intake.goals || intake.hair_length || intake.allergies) && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="font-semibold mb-3">Hair profile</h3>
              <div className="text-sm space-y-1.5">
                {intake.hair_type && <Field k="Type" v={intake.hair_type} />}
                {intake.hair_length && <Field k="Length" v={intake.hair_length} />}
                {intake.goals && <Field k="Goals" v={intake.goals} />}
                {intake.allergies && <Field k="Allergies" v={intake.allergies} />}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-lg">
            <div className="px-5 py-4 border-b border-border"><h3 className="font-semibold">Appointment history</h3></div>
            {appointments.length === 0 ? <div className="p-10 text-center text-muted-foreground text-sm">No appointments yet.</div> : (
              <div className="divide-y divide-border">
                {appointments.map((a) => (
                  <div key={a.id} className="px-5 py-4 flex items-center justify-between" data-testid={`history-${a.id}`}>
                    <div><div className="text-sm font-medium">{a.service_name}</div><div className="text-xs text-muted-foreground">{prettyDate(a.date)} · {a.start_time} · {a.stylist_name}</div></div>
                    <div className="flex items-center gap-3"><span className="text-sm text-muted-foreground">{money(a.price)}</span><Badge className={STATUS_STYLE[a.status]}>{a.status.replace("_", " ")}</Badge></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </CrmLayout>
  );
}

function Field({ k, v }) {
  return <div className="flex gap-2"><span className="text-muted-foreground w-24 shrink-0">{k}</span><span>{v}</span></div>;
}
